import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { formatCurrency } from '../../services/api';
import { rentabilidadGlobalAPI } from '../../modules/administracion/services/rentabilidadGlobalAPI';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const PALETTE = {
  primary: '#6366F1',
  primaryDark: '#4F46E5',
  success: '#10B981',
  danger: '#EF4444',
  warning: '#F59E0B',
  slate900: '#0F172A',
  slate700: '#334155',
  slate500: '#64748B',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
};

const formatHoras = (h) => {
  if (h == null || Number.isNaN(h)) return '—';
  return `${Number(h).toLocaleString('es-PE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h`;
};

const formatFecha = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('es-PE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Lima',
  });
};

const tablaStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  background: PALETTE.slate100,
  color: PALETTE.slate700,
  fontWeight: 700,
  borderBottom: '2px solid #E2E8F0',
};

const tdStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid #E2E8F0',
  color: PALETTE.slate900,
};

const SEVERIDAD = {
  critico: { bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.35)', color: PALETTE.danger, label: 'Crítico' },
  warn: { bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.35)', color: PALETTE.warning, label: 'Atención' },
  ok: { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.35)', color: PALETTE.success, label: 'Positivo' },
  info: { bg: PALETTE.slate50, border: '#E2E8F0', color: PALETTE.slate700, label: 'Info' },
};

const VEREDICTO = {
  critico: { bg: 'linear-gradient(135deg,#FEF2F2,#FEE2E2)', border: PALETTE.danger, icon: '⚠' },
  atencion: { bg: 'linear-gradient(135deg,#FFFBEB,#FEF3C7)', border: PALETTE.warning, icon: '◉' },
  estable: { bg: 'linear-gradient(135deg,#ECFDF5,#D1FAE5)', border: PALETTE.success, icon: '✓' },
  info: { bg: 'linear-gradient(135deg,#F8FAFC,#F1F5F9)', border: PALETTE.slate500, icon: 'i' },
};

const tipoParrafoBorder = (tipo) => {
  if (tipo === 'critico') return PALETTE.danger;
  if (tipo === 'warn') return PALETTE.warning;
  if (tipo === 'ok') return PALETTE.success;
  return PALETTE.primary;
};

const DashboardHorasMo = ({ periodo, periodoLabel }) => {
  const [vista, setVista] = useState('interpretado');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    if (!periodo || periodo.modo === 'total') return;
    setLoading(true);
    setError(null);
    try {
      const res = await rentabilidadGlobalAPI.getProduccion({
        meses: String(periodo.mes),
        anio: String(periodo.anio),
      });
      setData(res.data);
    } catch (err) {
      setError(err.message || 'Error al cargar reporte');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const chartActividades = useMemo(() => {
    const items = data?.analisis?.graficoActividades || [];
    return {
      labels: items.map((i) => i.label),
      datasets: [{
        label: 'Horas',
        data: items.map((i) => i.horas),
        backgroundColor: 'rgba(99,102,241,0.75)',
        borderRadius: 6,
      }],
    };
  }, [data]);

  const chartExpedientes = useMemo(() => {
    const items = data?.analisis?.graficoExpedientesCosto || [];
    return {
      labels: items.map((i) => i.label),
      datasets: [
        {
          label: 'Costo MO',
          data: items.map((i) => i.costoMo),
          backgroundColor: 'rgba(239,68,68,0.7)',
          borderRadius: 6,
        },
        {
          label: 'Contratado',
          data: items.map((i) => i.monto),
          backgroundColor: 'rgba(16,185,129,0.5)',
          borderRadius: 6,
        },
      ],
    };
  }, [data]);

  const chartPersonal = useMemo(() => {
    const items = data?.analisis?.graficoPersonalHoras || [];
    return {
      labels: items.map((i) => i.label),
      datasets: [
        {
          label: 'Horas reales',
          data: items.map((i) => i.horas),
          backgroundColor: 'rgba(99,102,241,0.75)',
          borderRadius: 6,
        },
        {
          label: 'Horas previstas',
          data: items.map((i) => i.deberian),
          backgroundColor: 'rgba(148,163,184,0.6)',
          borderRadius: 6,
        },
      ],
    };
  }, [data]);

  const chartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' } },
    scales: { y: { beginAtZero: true } },
  };

  if (periodo?.modo === 'total') {
    return (
      <div className="claude-card claude-fade-in" style={{
        background: '#fff', borderRadius: 20, padding: 28,
        border: '1px solid #E2E8F0', marginBottom: 22,
      }}>
        <p style={{ margin: 0, color: PALETTE.slate700 }}>
          El informe de mano de obra está disponible por mes. Usa <strong>Cambiar periodo</strong> y elige un mes específico.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
        <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={cargar}>
          Reintentar
        </button>
      </div>
    );
  }

  const { interpretado, analisis, bruto, totales } = data || {};

  /* ——— VISTA INTERPRETADA ——— */
  if (vista === 'interpretado') {
    return (
      <div className="claude-fade-in" style={{ marginBottom: 22 }}>
        <div className="claude-card" style={{
          background: '#fff', borderRadius: 20, padding: '24px 28px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 8px 24px rgba(15,23,42,0.05)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: PALETTE.primary, letterSpacing: 0.5, marginBottom: 6 }}>
                INFORME DE MANO DE OBRA
              </div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: PALETTE.slate900 }}>
                {periodoLabel}
              </h2>
            </div>
            <button
              type="button"
              className="claude-btn claude-btn-primary"
              style={{ background: PALETTE.primary, color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}
              onClick={() => setVista('analisis')}
            >
              Ver análisis →
            </button>
          </div>

          {/* Veredicto */}
          {interpretado?.veredicto && (
            <div style={{
              marginBottom: 22,
              padding: '18px 20px',
              borderRadius: 14,
              border: `1px solid ${(VEREDICTO[interpretado.veredicto.nivel] || VEREDICTO.info).border}`,
              background: (VEREDICTO[interpretado.veredicto.nivel] || VEREDICTO.info).bg,
            }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: PALETTE.slate700, marginBottom: 6 }}>
                {(VEREDICTO[interpretado.veredicto.nivel] || VEREDICTO.info).icon} VEREDICTO DEL MES
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: PALETTE.slate900, marginBottom: 8 }}>
                {interpretado.veredicto.titulo}
              </div>
              <p style={{ margin: 0, lineHeight: 1.65, color: PALETTE.slate700, fontSize: 14 }}>
                {interpretado.veredicto.resumen}
              </p>
            </div>
          )}

          {/* KPIs rápidos */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 24 }}>
            {[
              { label: 'Horas medibles', value: formatHoras(totales?.horasReales), accent: PALETTE.primary },
              { label: 'Costo MO', value: formatCurrency(totales?.costoMoTotal), accent: PALETTE.danger },
              { label: 'Entregas', value: totales?.partesCompletadas ?? 0, accent: PALETTE.success },
              { label: 'Datos fiables', value: totales?.calidadDatosPct != null ? `${totales.calidadDatosPct}%` : '—', accent: '#8B5CF6' },
              { label: 'A tiempo', value: totales?.pctEntregasATiempo != null ? `${totales.pctEntregasATiempo}%` : '—', accent: PALETTE.success },
              { label: 'Sin asignar', value: totales?.partesCompletadasSinAsignar ?? 0, accent: PALETTE.warning },
            ].map((k) => (
              <div key={k.label} style={{ background: PALETTE.slate50, borderRadius: 12, padding: 14, borderLeft: `4px solid ${k.accent}` }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: PALETTE.slate500 }}>{k.label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: PALETTE.slate900, marginTop: 4 }}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Análisis por bloques */}
          {(interpretado?.parrafos || []).map((p) => (
            <div
              key={p.titulo}
              style={{
                marginBottom: 18,
                paddingLeft: 14,
                borderLeft: `4px solid ${tipoParrafoBorder(p.tipo)}`,
              }}
            >
              <h3 style={{ fontSize: 15, fontWeight: 700, color: PALETTE.slate900, marginBottom: 8 }}>{p.titulo}</h3>
              <p style={{ margin: 0, lineHeight: 1.7, color: PALETTE.slate700, fontSize: 14 }}>{p.texto}</p>
            </div>
          ))}

          {/* Hallazgos */}
          {(interpretado?.hallazgos || []).length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Hallazgos clave</h3>
              <div style={{ display: 'grid', gap: 10 }}>
                {interpretado.hallazgos.map((h, idx) => {
                  const s = SEVERIDAD[h.severidad] || SEVERIDAD.info;
                  return (
                    <div
                      key={`${h.titulo}-${idx}`}
                      style={{
                        padding: '14px 16px',
                        borderRadius: 12,
                        background: s.bg,
                        border: `1px solid ${s.border}`,
                      }}
                    >
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                          color: s.color, textTransform: 'uppercase',
                        }}>
                          {s.label}
                        </span>
                        <strong style={{ fontSize: 14, color: PALETTE.slate900 }}>{h.titulo}</strong>
                      </div>
                      <p style={{ margin: '0 0 8px', fontSize: 13, lineHeight: 1.6, color: PALETTE.slate700 }}>{h.detalle}</p>
                      {h.accion && (
                        <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: s.color }}>
                          → {h.accion}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recomendaciones */}
          {(interpretado?.recomendaciones || []).length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>Qué hacer ahora</h3>
              <ol style={{ margin: 0, paddingLeft: 20, color: PALETTE.slate700, fontSize: 14, lineHeight: 1.8 }}>
                {interpretado.recomendaciones.map((r) => (
                  <li key={r} style={{ marginBottom: 6 }}>{r}</li>
                ))}
              </ol>
            </div>
          )}

          {/* Tabla expedientes */}
          {interpretado?.tablaExpedientes?.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Referencia numérica — expedientes</h3>
              <p style={{ fontSize: 12, color: PALETTE.slate500, marginBottom: 10 }}>
                Detalle de apoyo; la lectura principal está arriba en el análisis.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={tablaStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Expediente</th>
                      <th style={thStyle}>Cliente</th>
                      <th style={thStyle}>Horas</th>
                      <th style={thStyle}>Costo MO</th>
                      <th style={thStyle}>Contratado</th>
                      <th style={thStyle}>Margen MO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interpretado.tablaExpedientes.map((e) => (
                      <tr key={e.expedienteId}>
                        <td style={tdStyle}><strong>{e.numero}</strong></td>
                        <td style={tdStyle}>{e.cliente}</td>
                        <td style={tdStyle}>{formatHoras(e.horasReales)}</td>
                        <td style={tdStyle}>{formatCurrency(e.costoMo)}</td>
                        <td style={tdStyle}>{formatCurrency(e.montoContratado)}</td>
                        <td style={{
                          ...tdStyle,
                          color: e.margenMo < 0 ? PALETTE.danger : PALETTE.success,
                          fontWeight: 700,
                        }}>
                          {formatCurrency(e.margenMo)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Tabla personal */}
          {interpretado?.tablaPersonal?.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Referencia numérica — personal</h3>
              <p style={{ fontSize: 12, color: PALETTE.slate500, marginBottom: 10 }}>
                Horas y costo MO solo de partes con asignación formal.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={tablaStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Nombre</th>
                      <th style={thStyle}>Especialidad</th>
                      <th style={thStyle}>Horas reales</th>
                      <th style={thStyle}>Costo MO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interpretado.tablaPersonal.map((p) => (
                      <tr key={p.personalId}>
                        <td style={tdStyle}>{p.nombre}</td>
                        <td style={tdStyle}>{p.especialidad}</td>
                        <td style={tdStyle}>{formatHoras(p.horasReales)}</td>
                        <td style={tdStyle}>{formatCurrency(p.costoMo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Conclusión */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.06))',
            borderRadius: 14,
            padding: '18px 20px',
            border: '1px solid rgba(99,102,241,0.15)',
          }}>
            <h3 style={{ fontSize: 14, fontWeight: 800, color: PALETTE.primaryDark, marginBottom: 8 }}>
              Conclusión
            </h3>
            <p style={{ margin: 0, lineHeight: 1.7, color: PALETTE.slate700, fontSize: 14 }}>
              {interpretado?.conclusion}
            </p>
          </div>

          <p style={{ marginTop: 16, fontSize: 12, color: PALETTE.slate500 }}>
            Horario: lun–vie 8:00–18:00 y sáb 8:00–13:30 (sin 1 h de refrigerio). Costo con tasa horaria efectiva al {Math.round((totales?.pctProductivas || 0.7) * 100)}% productivo.
          </p>
        </div>
      </div>
    );
  }

  /* ——— VISTA ANÁLISIS ——— */
  if (vista === 'analisis') {
    return (
      <div className="claude-fade-in" style={{ marginBottom: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
          <button
            type="button"
            onClick={() => setVista('interpretado')}
            style={{ background: 'none', border: 'none', color: PALETTE.primary, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
          >
            ← Volver al informe
          </button>
          <button
            type="button"
            className="claude-btn claude-btn-outline"
            style={{ padding: '8px 16px', borderRadius: 10, border: `1px solid ${PALETTE.primary}`, color: PALETTE.primary, background: '#fff', fontWeight: 700, cursor: 'pointer' }}
            onClick={() => setVista('bruto')}
          >
            Ver información en bruto →
          </button>
        </div>

        <div className="claude-grid" style={{ display: 'grid', gap: 16 }}>
          <div className="claude-card" style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #E2E8F0' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700 }}>Horas por actividad</h3>
            <div style={{ height: 280 }}>
              {(analisis?.graficoActividades?.length > 0)
                ? <Bar data={chartActividades} options={chartOpts} />
                : <p style={{ color: PALETTE.slate500 }}>Sin datos de actividades completadas.</p>}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
            <div className="claude-card" style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #E2E8F0' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700 }}>Costo MO vs contratado</h3>
              <div style={{ height: 260 }}>
                {(analisis?.graficoExpedientesCosto?.length > 0)
                  ? <Bar data={chartExpedientes} options={chartOpts} />
                  : <p style={{ color: PALETTE.slate500 }}>Sin expedientes con costo MO.</p>}
              </div>
            </div>
            <div className="claude-card" style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #E2E8F0' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700 }}>Personal: real vs previsto</h3>
              <div style={{ height: 260 }}>
                {(analisis?.graficoPersonalHoras?.length > 0)
                  ? <Bar data={chartPersonal} options={chartOpts} />
                  : <p style={{ color: PALETTE.slate500 }}>Sin datos de personal.</p>}
              </div>
            </div>
          </div>

          <div className="claude-card" style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #E2E8F0' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700 }}>Resumen por expediente</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={tablaStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Expediente</th>
                    <th style={thStyle}>Horas reales</th>
                    <th style={thStyle}>Horas previstas</th>
                    <th style={thStyle}>Desvío</th>
                    <th style={thStyle}>Costo MO</th>
                    <th style={thStyle}>% MO / contrato</th>
                  </tr>
                </thead>
                <tbody>
                  {(analisis?.porExpediente || []).map((e) => (
                    <tr key={e.expedienteId}>
                      <td style={tdStyle}><strong>{e.numero}</strong></td>
                      <td style={tdStyle}>{formatHoras(e.horasReales)}</td>
                      <td style={tdStyle}>{formatHoras(e.horasDeberian)}</td>
                      <td style={{ ...tdStyle, color: e.desvioTotal > 0 ? PALETTE.danger : PALETTE.success }}>
                        {e.desvioTotal > 0 ? '+' : ''}{formatHoras(e.desvioTotal)}
                      </td>
                      <td style={tdStyle}>{formatCurrency(e.costoMo)}</td>
                      <td style={tdStyle}>{e.pctCostoMo != null ? `${e.pctCostoMo}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ——— VISTA BRUTO ——— */
  return (
    <div className="claude-fade-in" style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <button
          type="button"
          onClick={() => setVista('analisis')}
          style={{ background: 'none', border: 'none', color: PALETTE.primary, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
        >
          ← Volver al análisis
        </button>
        <span style={{ fontSize: 13, color: PALETTE.slate500 }}>
          {bruto?.filas?.length ?? 0} registros
        </span>
      </div>

      <div className="claude-card" style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #E2E8F0' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700 }}>Listado en bruto — asignaciones</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...tablaStyle, fontSize: 12 }}>
            <thead>
              <tr>
                <th style={thStyle}>Expediente</th>
                <th style={thStyle}>Personal</th>
                <th style={thStyle}>Actividad</th>
                <th style={thStyle}>Asignado</th>
                <th style={thStyle}>Límite</th>
                <th style={thStyle}>Entregado</th>
                <th style={thStyle}>Deberían</th>
                <th style={thStyle}>Reales</th>
                <th style={thStyle}>Desvío</th>
                <th style={thStyle}>Especialidad</th>
                <th style={thStyle}>Costo MO</th>
                <th style={thStyle}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {(bruto?.filas || []).map((f, idx) => (
                <tr key={`${f.expediente}-${f.actividad}-${idx}`}>
                  <td style={tdStyle}>{f.expediente}</td>
                  <td style={tdStyle}>{f.personal}</td>
                  <td style={tdStyle}>{f.actividad}</td>
                  <td style={tdStyle}>
                    {formatFecha(f.fechaAsignacion)}
                    {f.asignacionEstimada && (
                      <span style={{ display: 'block', fontSize: 10, color: PALETTE.warning }}>estimada</span>
                    )}
                  </td>
                  <td style={tdStyle}>{formatFecha(f.fechaLimite)}</td>
                  <td style={tdStyle}>{formatFecha(f.fechaCompletado)}</td>
                  <td style={tdStyle}>{formatHoras(f.horasDeberian)}</td>
                  <td style={tdStyle}>{formatHoras(f.horasReales)}</td>
                  <td style={tdStyle}>{f.desvio != null ? formatHoras(f.desvio) : '—'}</td>
                  <td style={tdStyle}>{f.especialidad}</td>
                  <td style={tdStyle}>{f.costoMo != null ? formatCurrency(f.costoMo) : '—'}</td>
                  <td style={tdStyle}>{f.estado}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardHorasMo;
